import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Position } from '@turf/turf';
import { mapCenterState } from '../../../../../../../app/redux/retrievers/retrievers';
import {
    calculateSlope,
    calculateDirectionalSlopes,
    findHighestAndLowestElevationPoints,
    findHighestDirectionalPoints,
    findLowestDirectionalPoints,
    generatePointsInPolygon,
    convertPolygonCoordsToLatLngElevationArray,
} from '../../../../../../../core/utils/map.utils';
import {
    Coordinates,
    CoordinatesWithElevation,
    MainSlopePoints,
    PolygonElevationPoints,
    SlopeDistanceLinePoint,
    SlopeInfo,
} from '../../../../../../../core/types/types';

import { generateSlopeDistanceLinePoints } from './functions/generateSlopeDistanceLinePoints';
import { GetElevationDataApi } from '../../../../../../../core/data/api/elevation-data/get/GetElevationDataApi';

import SlopeServicePopup from './components/SlopeServicePopup';
import SlopeDistanceLines from './components/SlopeDistanceLines';
import DirectionalPointMarkers from './components/DirectionalPointMarkers';
import { filterNonPairedPoints } from './functions/filterNonPairedPoints';
import { generateHighestLowestElevationPointsObject } from './functions/generateHighestLowestElevationPointsObject';
import MainSlopePointMarkers from './components/MainSlopePointMarkers';
import MainSlopeLine from './components/MainSlopeLine';
import { sendDataToParent } from '../../../../../base/functions/sendDataToParent';

interface SlopeServiceLayerProps {
    polygonCoords: Coordinates[];
}
const SlopeServiceLayer: React.FC<SlopeServiceLayerProps> = ({ polygonCoords }) => {
    const mapCenterCoords = useSelector(mapCenterState);
    const [generatedPoints, setGeneratedPoints] = useState<any>();
    const [highestPolygonPoints, setHighestPolygonPoints] = useState<Position[]>();
    const [lowestPolygonPoints, setLowestPolygonPoints] = useState<Position[]>();
    const [highestAndLowestElevationPoints, setHighestAndLowestElevation] = useState<PolygonElevationPoints>();
    const [mainSlopePoints, setMainSlopePoints] = useState<MainSlopePoints>();
    const [mainSlopeInfo, setMainSlopeInfo] = useState<SlopeInfo>();
    const [polygonDirectionalSlopesInfo, setPolygonDirectionalSlopesInfo] = useState<SlopeInfo[]>();
    const [slopeDistanceLinePoints, setSlopeDistanceLinePoints] = useState<SlopeDistanceLinePoint[]>();
    // const [highestAndLowestDirectionalPoints, setHighestAndLowestDirectionalPoints] = useState<any>();

    async function runSlopeService() {
        const generatedPolygonPoints = generatePointsInPolygon(polygonCoords);
        // setGeneratedPoints(generatedPolygonPoints.generatedPointsCoordinatesArray);
        const polygonCoordsWithElevation = await GetElevationDataApi(
            generatedPolygonPoints.generatedPointsCoordinatesLatLngs,
        );

        setHighestAndLowestElevation(findHighestAndLowestElevationPoints(polygonCoordsWithElevation));

        const highestPolygonDirectionalPoints = findHighestDirectionalPoints(polygonCoordsWithElevation, polygonCoords);

        const lowestPolygonDirectionalPoints = findLowestDirectionalPoints(polygonCoordsWithElevation, polygonCoords);

        const { highestFilteredPolygonDirectionalPoints, lowestFilteredPolygonDirectionalPoints } = filterNonPairedPoints(
            highestPolygonDirectionalPoints,
            lowestPolygonDirectionalPoints,
        );

        /*convert points object to array of objects and then convert them to lat lng array*/
        setHighestPolygonPoints(
            convertPolygonCoordsToLatLngElevationArray(
                Object.entries(highestFilteredPolygonDirectionalPoints).map(([key, value]) => ({
                    direction: key,
                    ...value,
                })),
            ),
        );
        // console.log('highest points: ', highestPolygonDirectionalPoints);

        setLowestPolygonPoints(
            convertPolygonCoordsToLatLngElevationArray(
                Object.entries(lowestFilteredPolygonDirectionalPoints).map(([key, value]) => ({
                    direction: key,
                    ...value,
                })),
            ),
        );
        // console.log('lowest points: ', lowestPolygonDirectionalPoints);

        setSlopeDistanceLinePoints(
            generateSlopeDistanceLinePoints(highestFilteredPolygonDirectionalPoints, lowestFilteredPolygonDirectionalPoints),
        );

        const slopesOfOpposingHighestAndLowestDirectionalPoints = calculateDirectionalSlopes(
            highestPolygonDirectionalPoints,
            lowestPolygonDirectionalPoints,
        );
        // console.log('slope check: ', slopesOfOpposingHighestAndLowestDirectionalPoints);

        /*convert slopes object to array of objects and then set them to the state*/
        setPolygonDirectionalSlopesInfo(
            Object.entries(slopesOfOpposingHighestAndLowestDirectionalPoints).map(([key, value]) => ({
                direction: key,
                slopePercentage: value.slopePercentage,
                slopeAngle: value.slopeAngle,
                slopeDirection: value.slopeDirection,
                slopePointsDistance: value.slopePointsDistance,
                color: value.color,
            })),
        );
    }
    useEffect(() => {
        runSlopeService();
    }, [polygonCoords]);

    useEffect(() => {
        if (highestAndLowestElevationPoints) {
            setMainSlopePoints(generateHighestLowestElevationPointsObject(highestAndLowestElevationPoints));
        }
    }, [highestAndLowestElevationPoints]);

    useEffect(() => {
        const mainSlope = mainSlopePoints && calculateSlope(mainSlopePoints.highest, mainSlopePoints.lowest);
        setMainSlopeInfo(mainSlope);
    }, [mainSlopePoints]);

    useEffect(() => {
        sendDataToParent({ type: 'slope-info', slopeInfo: mainSlopeInfo });
    }, [mainSlopeInfo]);

    return (
        <>
            {mainSlopePoints && <MainSlopePointMarkers mainSlopePoints={mainSlopePoints} />}
            {mainSlopePoints && <MainSlopeLine mainSlopePoints={mainSlopePoints} />}
            {highestPolygonPoints && <DirectionalPointMarkers directionalPoints={highestPolygonPoints} markerColor="red" />}
            {lowestPolygonPoints && <DirectionalPointMarkers directionalPoints={lowestPolygonPoints} markerColor="green" />}
            {slopeDistanceLinePoints && <SlopeDistanceLines slopeDistanceLinePoints={slopeDistanceLinePoints} />}
            {polygonDirectionalSlopesInfo && mainSlopeInfo && (
                <SlopeServicePopup
                    mapCenter={mapCenterCoords}
                    polygonDirectionalSlopesInfo={polygonDirectionalSlopesInfo}
                    mainSlopeInfo={mainSlopeInfo}
                />
            )}
        </>
    );
};

export default SlopeServiceLayer;

// setHighestAndLowestDirectionalPoints({
//     highest: highestPolygonDirectionalPoints,
//     lowest: lowestPolygonDirectionalPoints,
// });
// setGeneratedPoints(lowestDirectionalPointsLatLngArray);
//console.log('slope coords result: ', polygonCoordsWithElevation);
// console.log('highest & lowest points state: ', highestAndLowestDirectionalPoints);
// console.log('highest points test: ', highestPolygonPoints);
// console.log('lowest points test: ', lowestPolygonPoints);
// console.log('slopes test: ', slopesBetweenOpposingHighestAndLowestDirectionalPoints);
// console.log('all polygon slopes:', polygonDirectionalSlopes);

// generatePointsBetweenGivenPoints([
//     {
//         lat: highestAndLowestElevationPoints.highest.latitude,
//         lng: highestAndLowestElevationPoints.highest.longitude,
//         elevation:highestAndLowestElevationPoints.highest.elevation,
//     },
//     {
//         lat: highestAndLowestElevationPoints.lowest.latitude,
//         lng: highestAndLowestElevationPoints.lowest.longitude,
//         elevation:highestAndLowestElevationPoints.lowest.elevation,
//     },
// ]);
{
    /* {generatedPoints &&
                generatedPoints.map((point: any) => {
                    if (point[0] != null && point[1] != null) return <Marker position={point} icon={icon()} />;
                })} */
}
